import Link from 'next/link'
import { FormEvent, useEffect, useRef, useState } from 'react'
import { Button } from 'components/Button'
import { Input, Card } from 'components/ui'
import { VLink } from 'components/VLink'
import { api, ResponseError } from 'lib/api'
import { debug, logError } from 'lib/logging'
import { errorToast } from 'lib/toast'
import { useMe } from 'lib/useMe'

/**
 * Login page component
 * @description Handles user authentication with email/password and email verification code
 * @returns {JSX.Element} The login page with authentication forms
 */
export default function Login() {
  let [email, setEmail] = useState<string>()
  let [password, setPassword] = useState<string>()
  let formRef = useRef<HTMLFormElement>(null)

  const me = useMe()
  useEffect(() => {
    if (me) document.location = `/`
  }, [me])

  /**
   * Checks if all verification code inputs are filled
   * @description Validates that all 6 verification code inputs contain single digits
   * @returns {void}
   */
  function checkSubmit() {
    let ready = [1, 2, 3, 4, 5, 6].every((i) => {
      let d = document.querySelector(`#code-${i}`)?.[`value`]
      return d?.match(/^[0-9]$/)
    })
    debug(`auth`, `checkSubmit, ready?`, ready)
    if (ready) {
      void submitCode()
    }
  }

  /**
   * Submits the email verification code
   * @description Collects the 6-digit verification code from input fields and submits to API
   * @returns {Promise<void>} Promise that resolves when authentication is complete
   */
  let submitCode = async () => {
    debug(`auth`, `submitting code`)
    let code = [1, 2, 3, 4, 5, 6]
      .map((i) => {
        return document.querySelector(`#code-${i}`)?.[`value`]
      })
      .join(``)
    await api.post(`/api/auth/login`, { code, email, password }).then(() => {
      document.location = `/`
    })
  }

  /**
   * Handles login form submission
   * @description Processes login credentials and handles email verification flow
   * @param {FormEvent<HTMLFormElement>} event - The form submission event
   * @returns {Promise<boolean>} Returns false to prevent default form submission
   */
  let submitLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    let formData = new FormData(formRef.current!)

    let req = {
      email: formData.get(`email`),
      password: formData.get(`password`),
    }

    debug(`login`, `form data`, req)

    try {
      await api.post(`/api/auth/login`, req)
      document.location = `/account`
    } catch (err: any) {
      if (err instanceof ResponseError && err.status === 412) {
        setEmail((formData.get(`email`) as string) || undefined)
        setPassword((formData.get(`password`) as string) || undefined)
      } else {
        logError(`login error`, err?.message)
        errorToast(err.message)
      }
    }

    return false
  }

  /**
   * Handles forgot password navigation
   * @description Redirects to forgot password page with email if available
   * @returns {Promise<void>} Promise that resolves when navigation is complete
   */
  let forgot = async () => {
    let email = new FormData(formRef.current!).get(`email`)
    if (email) {
      document.location = `/forgot?email=${email}`
    } else {
      document.location = `/forgot`
    }
  }

  return (
    <div id='login-page' className='center-all min-h-screen bg-slate-900 text-white'>
      {email && password ? (
        <Card mode='primary' className='max-w-2xl'>
          <div className='font-weight-bold pb-4 text-center'>
            Please verify your email by entering the code that was just emailed to you.
          </div>

          <div>
            {[1, 2, 3, 4, 5, 6].map((i) => {
              return (
                <input
                  key={i}
                  style={{ width: `100px`, height: `100px` }}
                  className='border-5 m-1 border border-cyan-400 text-center text-5xl text-slate-800 focus:border-cyan-300 focus:outline-none focus:ring-2 focus:ring-cyan-400/50'
                  id={`code-` + i}
                  type='tel'
                  autoComplete='off'
                  placeholder='-'
                  maxLength={1}
                  tabIndex={i}
                  onPaste={(event) => {
                    event.preventDefault()
                    let code = event.clipboardData.getData(`text`)
                    if (!code.match(/^[0-9]+$/)) return
                    let digits = code.split(``)
                    digits.forEach((d, j) => {
                      document.querySelector(`#code-${j + 1}`)?.setAttribute(`value`, d || ``)
                    })
                    checkSubmit()
                  }}
                  onKeyDown={(event) => {
                    // keep non-numbers from being entered
                    if (
                      event.metaKey ||
                      event.ctrlKey ||
                      event.altKey ||
                      [`Tab`, `Backspace`, `Delete`].includes(event.key)
                    )
                      return
                    if (!event.key.match(/[0-9]/)) return event.preventDefault()
                  }}
                  onKeyUp={(event) => {
                    if (!event.key.match(/[0-9]/)) return
                    // @ts-ignore
                    document.querySelector(`#code-${i + 1}`)?.focus()
                    checkSubmit()
                  }}
                />
              )
            })}
          </div>
        </Card>
      ) : (
        <div className='flex flex-col items-center'>
          <Card mode='primary' className='w-[400px]'>
            <form ref={formRef} onSubmit={submitLogin}>
              <div className='mb-8'>
                <label htmlFor='email' className='block text-sm font-medium text-slate-100 mb-1'>
                  Email Address
                </label>
                <Input
                  name='email'
                  id='email'
                  type='email'
                  mode='primary'
                  fullWidth={true}
                  glow={true}
                  required
                  defaultValue={process.env.DEFAULT_EMAIL}
                />
              </div>
              <div className='mb-8'>
                <label htmlFor='password' className='block text-sm font-medium text-slate-100 mb-1'>
                  Password
                </label>
                <Input
                  type='password'
                  id='password'
                  name='password'
                  mode='secondary'
                  fullWidth={true}
                  glow={true}
                  aria-label='password'
                  required
                  defaultValue={process.env.DEFAULT_PASSWORD}
                />
              </div>
              <div className=''>
                <Button type='submit'>Log In</Button>
              </div>
            </form>
          </Card>
          <div className='text-center'>
            <p className='my-5 text-center text-slate-300'>
              By signing up for a CheapVeep account you are agreeing to the{` `}
              <Link href='/privacy' className='font-bold text-cyan-400 underline'>
                Privacy Policy
              </Link>
              {` `}
              and{` `}
              <Link className='font-bold text-cyan-400 underline' href='/terms'>
                Terms of Service
              </Link>
              .
            </p>
            <div>
              <VLink href='/signup' className='text-white'>
                Need an account?
              </VLink>
              {` `}
              or{` `}
              <Link href='/forgot' className='text-white' onClick={forgot}>
                Forgot your password?
              </Link>
            </div>
            <div></div>
          </div>
        </div>
      )}
    </div>
  )
}
